import React from 'react';
import { Box } from '@mui/material';

/**
 * רנדור עיצוב הטקסט של וואטסאפ בבועת ההודעה (28.10).
 *
 * עד כה הצ'אט הציג את סימוני העיצוב כתווים: לקוח ששלח `*דחוף*` נראה אצלנו עם
 * כוכביות, וחתימה אישית שנשלחת בנטוי נראתה לנציג עם קווים תחתונים. הפענוח כאן
 * מיישר את התצוגה שלנו למה שהצד השני רואה בפועל.
 *
 * הסימונים הנתמכים זהים לאלה של וואטסאפ: ‏`*מודגש*`, ‏`_נטוי_`, ‏`~חוצה~`
 * ו-```` ```רוחב-קבוע``` ````. אפשרי קינון (למשל `*_שניהם_*`).
 *
 * הפענוח (`tokenizeWhatsappFormatting`) מופרד מהרינדור בכוונה — הביטויים
 * הקבועים הם החלק העדין, והם מכוסים בבדיקות ב-whatsappTextFormatting.test.ts.
 */

export type WhatsappFormatVariant = 'bold' | 'italic' | 'strike' | 'mono';

export type WhatsappTextNode =
  | string
  | { variant: WhatsappFormatVariant; children: WhatsappTextNode[] };

/**
 * כלל לכל סימון. לכל ביטוי שתי קבוצות לכידה בדיוק:
 * קבוצה 1 — התו שלפני הסימן (נפלט כטקסט רגיל), קבוצה 2 — התוכן שבין הסימנים.
 *
 * שתי החמרות מכוונות, שתיהן מחקות את וואטסאפ ומונעות זיהוי שגוי:
 * ‏(א) הסימן חייב לשבת על גבול מילה — אחרת `snake_case_name` היה הופך לנטוי
 *     ו-`5*3` היה הופך למודגש;
 * ‏(ב) אין רווח צמוד לסימן מבפנים, והתוכן אינו חוצה שורות (למעט רוחב קבוע,
 *     שבוואטסאפ כן נפרס על כמה שורות).
 *
 * ההטיה היא לכיוון זיהוי מחמיר: אם נעצב משהו שוואטסאפ לא מעצב, נציג ללקוח
 * מצג שגוי; אם לא נעצב משהו שכן מעוצב — נשארנו עם התצוגה שהייתה עד היום.
 *
 * במכוון בלי lookbehind: הוא נתמך רק מ-Safari 16.4, ושגיאת תחביר בביטוי קבוע
 * נזרקת בזמן טעינת המודול — כלומר מסך לבן לכל האפליקציה, לא פגם מקומי.
 */
const RULES: ReadonlyArray<{ variant: WhatsappFormatVariant; re: RegExp }> = [
  //* הקבוצה הריקה שומרת על אותו מבנה קבוצות; לגרשיים אין דרישת גבול מילה
  { variant: 'mono', re: /()```(?=[^\s`])([\s\S]*?[^\s`])```/ },
  { variant: 'bold', re: /(^|[^\p{L}\p{N}])\*(?=[^\s*])([^\n*]*[^\s*])\*(?![\p{L}\p{N}])/u },
  { variant: 'italic', re: /(^|[^\p{L}\p{N}])_(?=[^\s_])([^\n_]*[^\s_])_(?![\p{L}\p{N}])/u },
  { variant: 'strike', re: /(^|[^\p{L}\p{N}])~(?=[^\s~])([^\n~]*[^\s~])~(?![\p{L}\p{N}])/u },
];

/** תקרת קינון — מגן מפני טקסט מוזר שיגרום לרקורסיה עמוקה */
const MAX_DEPTH = 4;

/**
 * מפרק טקסט לעץ של מקטעים מעוצבים. טקסט בלי סימוני עיצוב חוזר כמקטע אחד,
 * כך שהמסלול הנפוץ (הודעה רגילה) לא יוצר כלום.
 */
export function tokenizeWhatsappFormatting(text: string, depth = 0): WhatsappTextNode[] {
  if (!text) return [];
  if (depth >= MAX_DEPTH) return [text];

  const out: WhatsappTextNode[] = [];
  let remaining = text;

  while (remaining) {
    let best: { variant: WhatsappFormatVariant; match: RegExpExecArray; markerStart: number } | null =
      null;

    for (const rule of RULES) {
      //* ללא דגל g — exec תמיד מתחיל מ-0, ולכן אובייקטי ה-RegExp המשותפים חסרי מצב
      const match = rule.re.exec(remaining);
      if (!match) continue;
      const markerStart = match.index + match[1].length;
      if (!best || markerStart < best.markerStart) {
        best = { variant: rule.variant, match, markerStart };
      }
    }

    if (!best) {
      out.push(remaining);
      break;
    }

    const { match, markerStart, variant } = best;
    if (markerStart > 0) out.push(remaining.slice(0, markerStart));
    out.push({ variant, children: tokenizeWhatsappFormatting(match[2], depth + 1) });
    remaining = remaining.slice(match.index + match[0].length);
  }

  return out;
}

const VARIANT_SX: Record<WhatsappFormatVariant, Record<string, unknown>> = {
  bold: { fontWeight: 700 },
  italic: { fontStyle: 'italic' },
  strike: { textDecoration: 'line-through' },
  //* רוחב קבוע מוקטן מעט כדי שלא יבלוט מול שאר השורה
  mono: { fontFamily: 'monospace', fontSize: '0.92em' },
};

function renderNodes(nodes: WhatsappTextNode[], keyPrefix: string): React.ReactNode[] {
  return nodes.map((node, index) => {
    if (typeof node === 'string') return node;
    const key = `${keyPrefix}-${index}`;
    return (
      <Box component="span" key={key} sx={VARIANT_SX[node.variant]}>
        {renderNodes(node.children, key)}
      </Box>
    );
  });
}

/** מרנדר מקטע טקסט עם עיצוב וואטסאפ לצמתי React */
export function renderWhatsappFormattedText(text: string, keyPrefix: string): React.ReactNode[] {
  return renderNodes(tokenizeWhatsappFormatting(text), keyPrefix);
}
